import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  computed,
  input
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { FieldsetModule } from 'primeng/fieldset';
import { DropdownModule } from 'primeng/dropdown';
import { DatePickerModule } from 'primeng/datepicker';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextarea } from 'primeng/inputtextarea';
import { TooltipModule } from 'primeng/tooltip';
import { TipoCita } from 'src/app/domain/model/agenda/agenda';
import { Oficina } from 'src/app/domain/model/catalogo/oficina';
import { TipoAsesorReal } from 'src/app/domain/model/catalogo/tipo-asesor-real';
import { TIPOS_CITA_IDS } from 'src/app/common/scheduling-constants';
import { sanitizeTextarea } from 'src/app/common/input-utils';
import { HoraHintComponent } from 'src/app/presentation/shared/hora-hint/hora-hint.component';

export interface PmFiltrosForm {
  tipoCita: number | null;
  fechaCita: Date | null;
  horaInicio: Date | null;
  duracionTime: Date | null;
  oficina: number | null;
  observaciones: string;
  tiposAsesorSeleccionados: string[];
  // Campos condicionales (Legacy: tipos CAMPO_COMPLEMENTARIO, INFORME_COMPLEMENTARIO)
  beneficiarioId: number | null;
  beneficiarioContrato: string;
  beneficiarioIdentificacion: string;
  beneficiarioNombre: string;
  centroTrabajoId: number | null;
  centroTrabajoNombre: string;
  tareaId: number | null;
  tareaNombre: string;
}

/**
 * Componente Dumb: Formulario de filtros
 *
 * Muestra los campos del formulario y emite cambios.
 * NO tiene lógica de negocio ni servicios inyectados.
 */
@Component({
  selector: 'app-pm-filtros',
  standalone: true,
  imports: [
    HoraHintComponent,
    CommonModule,
    FormsModule,
    ButtonModule,
    FieldsetModule,
    DropdownModule,
    DatePickerModule,
    CheckboxModule,
    InputTextModule,
    InputTextarea,
    TooltipModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-fieldset legend="Filtros" [toggleable]="true" styleClass="mb-4">
      <div class="grid grid-cols-12 gap-3 sm:gap-4">
        <!-- Tipo de Cita -->
        <div class="col-span-12 md:col-span-6">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Tipo de Cita <span class="text-red-500">*</span>
          </label>
          <p-dropdown
            [options]="tiposCita"
            [ngModel]="form.tipoCita"
            (ngModelChange)="onTipoCitaChange($event)"
            optionLabel="dstipo"
            optionValue="nmconsecutivo"
            placeholder="--Seleccione una opción--"
            [filter]="true"
            filterBy="dstipo"
            [style]="{ width: '100%' }"
            [disabled]="readonly"
          >
          </p-dropdown>
        </div>

        <!-- ================================================================ -->
        <!-- CAMPOS CONDICIONALES: Solo visibles para CAMPO_COMPLEMENTARIO (42) e INFORME_COMPLEMENTARIO (43) -->
        <!-- Legacy: Aparecen justo después de Tipo de Cita, antes de Fecha -->
        <!-- ================================================================ -->
        @if (mostrarCamposComplementarios) {
          <!-- Cliente (Legacy: idBeneficiariosBuscadorView) -->
          <div class="col-span-12">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Cliente <span class="text-red-500">*</span>
            </label>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-2">
              <input
                pInputText
                [ngModel]="form.beneficiarioContrato"
                placeholder="Contrato"
                readonly
                class="w-full cursor-pointer"
                (click)="buscarBeneficiario.emit()"
              />
              <input
                pInputText
                [ngModel]="form.beneficiarioIdentificacion"
                placeholder="Identificación"
                readonly
                class="w-full"
              />
              <div class="flex items-stretch gap-1">
                <input
                  pInputText
                  [ngModel]="form.beneficiarioNombre"
                  placeholder="Nombre"
                  readonly
                  class="w-full flex-1 cursor-pointer"
                  (click)="buscarBeneficiario.emit()"
                />
                <button
                  pButton
                  icon="pi pi-search"
                  class="p-button-text min-w-[44px] min-h-[44px]"
                  pTooltip="Buscar cliente"
                  (click)="buscarBeneficiario.emit()"
                ></button>
              </div>
            </div>
          </div>

          <!-- Centro de Trabajo -->
          <div class="col-span-12 md:col-span-6">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Centro de Trabajo <span class="text-red-500">*</span>
            </label>
            <div class="flex items-stretch gap-1">
              <input
                pInputText
                [ngModel]="form.centroTrabajoNombre"
                placeholder="Seleccione un centro de trabajo"
                readonly
                class="w-full flex-1 cursor-pointer"
                (click)="onBuscarCentroTrabajo()"
              />
              <button
                pButton
                icon="pi pi-search"
                class="p-button-text min-w-[44px] min-h-[44px]"
                pTooltip="Buscar centro de trabajo"
                (click)="onBuscarCentroTrabajo()"
                [disabled]="!form.beneficiarioId"
              ></button>
            </div>
          </div>

          <!-- Nombre Tarea -->
          <div class="col-span-12 md:col-span-6">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Nombre Tarea <span class="text-red-500">*</span>
            </label>
            <div class="flex items-stretch gap-1">
              <input
                pInputText
                [ngModel]="form.tareaNombre"
                placeholder="Seleccione una tarea"
                readonly
                class="w-full flex-1 cursor-pointer"
                (click)="buscarTarea.emit()"
              />
              <button
                pButton
                icon="pi pi-search"
                class="p-button-text min-w-[44px] min-h-[44px]"
                pTooltip="Buscar tarea"
                (click)="buscarTarea.emit()"
              ></button>
            </div>
          </div>
        }

        <!-- Fecha de Cita (Legacy: activarCampos bloquea este campo) -->
        <div class="col-span-12 sm:col-span-6 lg:col-span-3">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Fecha Cita <span class="text-red-500">*</span>
          </label>
          <p-datepicker
            [ngModel]="form.fechaCita"
            (ngModelChange)="onFieldChange('fechaCita', $event)"
            [showTime]="false"
            dateFormat="yy/mm/dd"
            [style]="{ width: '100%' }"
            [disabled]="readonly || readonlyFechaHora"
            [showTransitionOptions]="'0ms'"
            [hideTransitionOptions]="'0ms'"
            panelStyleClass="pm-calendar-panel"
          >
          </p-datepicker>
        </div>

        <!-- Hora Inicio (Legacy: activarCampos bloquea este campo) -->
        <div class="col-span-12 sm:col-span-6 lg:col-span-3">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Hora Inicio <span class="text-red-500">*</span>
          </label>
          <p-datepicker
            [ngModel]="form.horaInicio"
            (ngModelChange)="onFieldChange('horaInicio', $event)"
            [timeOnly]="true"
            [hourFormat]="'24'"
            [style]="{ width: '100%' }"
            [disabled]="readonly || readonlyFechaHora"
            [showTransitionOptions]="'0ms'"
            [hideTransitionOptions]="'0ms'"
            panelStyleClass="pm-calendar-panel"
          >
          </p-datepicker>
          <app-hora-hint [id]="'pm-hora-inicio-ayuda'" />
        </div>

        <!-- Duración (Legacy: activarCampos bloquea este campo) -->
        <div class="col-span-12 sm:col-span-6 lg:col-span-3">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Duración <span class="text-red-500">*</span>
          </label>
          <p-datepicker
            [ngModel]="form.duracionTime"
            (ngModelChange)="onFieldChange('duracionTime', $event)"
            [timeOnly]="true"
            [hourFormat]="'24'"
            [defaultDate]="defaultDuracion"
            [style]="{ width: '100%' }"
            [disabled]="readonly || readonlyFechaHora"
            [showTransitionOptions]="'0ms'"
            [hideTransitionOptions]="'0ms'"
            panelStyleClass="pm-calendar-panel"
          >
          </p-datepicker>
        </div>

        <!-- Oficina -->
        <div class="col-span-12 md:col-span-6">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Oficina <span class="text-red-500">*</span>
          </label>
          <p-dropdown
            [options]="oficinas"
            [ngModel]="form.oficina"
            (ngModelChange)="onFieldChange('oficina', $event)"
            optionLabel="dsoficina"
            optionValue="nmconsecutivo"
            placeholder="--Seleccione una opción--"
            [filter]="true"
            filterBy="dsoficina"
            [style]="{ width: '100%' }"
            [disabled]="readonly"
          >
          </p-dropdown>
        </div>

        <!-- Tipo Asesor -->
        <div class="col-span-12 md:col-span-6">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Tipo Asesor <span class="text-red-500">*</span>
          </label>
          <div
            class="border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-800 max-h-32 overflow-y-auto"
          >
            @for (tipo of tiposAsesor; track tipo.cdtipoReal) {
              <div class="flex items-center py-1">
                <p-checkbox
                  [ngModel]="form.tiposAsesorSeleccionados"
                  (ngModelChange)="
                    onFieldChange('tiposAsesorSeleccionados', $event)
                  "
                  [value]="tipo.cdtipoReal"
                  [inputId]="'tipo_' + tipo.cdtipoReal"
                  [disabled]="readonly"
                >
                </p-checkbox>
                <label
                  [for]="'tipo_' + tipo.cdtipoReal"
                  class="ml-2 text-sm cursor-pointer"
                >
                  {{ tipo.dstipo }}
                </label>
              </div>
            }
          </div>
        </div>

        <!-- Asesores -->
        <div class="col-span-12 md:col-span-6">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Consultores <span class="text-red-500">*</span>
          </label>
          <div
            class="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 p-2 sm:p-0"
          >
            <span
              class="flex-1 px-1 sm:px-3 py-1 sm:py-2 text-gray-600 dark:text-gray-400 text-sm"
            >
              {{
                cantidadAsesores > 0
                  ? cantidadAsesores + ' consultor(es) seleccionado(s)'
                  : ''
              }}
            </span>
            <div class="flex w-full sm:w-auto gap-2 sm:gap-0 sm:pl-2">
              <button
                pButton
                label="Seleccionar"
                icon="pi pi-search"
                class="p-button-text p-button-sm min-h-[44px] flex-1 sm:flex-none"
                (click)="buscarAsesores.emit()"
                [disabled]="readonly"
              ></button>
              <button
                pButton
                label="Limpiar"
                icon="pi pi-times"
                class="p-button-text p-button-sm min-h-[44px] flex-1 sm:flex-none"
                pTooltip="Limpiar consultores seleccionados"
                (click)="onLimpiarConsultores()"
              ></button>
            </div>
          </div>
        </div>

        <!-- Observaciones (Legacy: activarCampos bloquea este campo) -->
        <div class="col-span-12">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Observaciones
          </label>
          <textarea
            pInputTextarea
            [ngModel]="form.observaciones"
            (ngModelChange)="onFieldChange('observaciones', sanitizeTextarea($event))"
            rows="2"
            [style]="{ width: '100%' }"
            [disabled]="readonly || readonlyFechaHora"
          >
          </textarea>
        </div>
      </div>
    </p-fieldset>
  `
})
export class PmFiltrosComponent {
  /** Sanitización XSS/SQLi (seguridad-girop §3) para textarea de texto libre */
  sanitizeTextarea = sanitizeTextarea;

  @Input() form: PmFiltrosForm = {
    tipoCita: null,
    fechaCita: null,
    horaInicio: null,
    duracionTime: null,
    oficina: null,
    observaciones: '',
    tiposAsesorSeleccionados: [],
    // Campos condicionales
    beneficiarioId: null,
    beneficiarioContrato: '',
    beneficiarioIdentificacion: '',
    beneficiarioNombre: '',
    centroTrabajoId: null,
    centroTrabajoNombre: '',
    tareaId: null,
    tareaNombre: ''
  };
  @Input() tiposCita: TipoCita[] = [];
  @Input() oficinas: Oficina[] = [];
  @Input() tiposAsesor: TipoAsesorReal[] = [];
  @Input() cantidadAsesores = 0;
  /** Legacy: activarCampos(false) bloquea todos los campos después de programar */
  @Input() readonly = false;
  /** Legacy: activarCampos bloquea solo fecha/hora/duración/obs cuando hay asesores ya programados */
  @Input() readonlyFechaHora = false;

  @Output() formChange = new EventEmitter<Partial<PmFiltrosForm>>();
  @Output() buscarAsesores = new EventEmitter<void>();
  @Output() limpiarConsultores = new EventEmitter<void>();
  // Outputs para campos condicionales (Legacy: tableComplementarios)
  @Output() buscarBeneficiario = new EventEmitter<void>();
  @Output() buscarCentroTrabajo = new EventEmitter<void>();
  @Output() buscarTarea = new EventEmitter<void>();

  /** Fecha default para duración: 00:00 */
  readonly defaultDuracion = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  })();

  /**
   * Legacy: tableComplementarios visible cuando:
   * - TiposDeCita.CAMPO_COMPLEMENTARIO (42)
   * - TiposDeCita.INFORME_COMPLEMENTARIO (43)
   */
  get mostrarCamposComplementarios(): boolean {
    return (
      this.form.tipoCita === TIPOS_CITA_IDS.CAMPO_COMPLEMENTARIO ||
      this.form.tipoCita === TIPOS_CITA_IDS.INFORME_COMPLEMENTARIO
    );
  }

  onFieldChange(campo: keyof PmFiltrosForm, valor: unknown): void {
    this.formChange.emit({ [campo]: valor } as Partial<PmFiltrosForm>);
  }

  onLimpiarConsultores(): void {
    this.limpiarConsultores.emit();
  }

  /**
   * Cuando cambia el tipo de cita, limpiar campos condicionales si ya no aplican
   * Legacy: tableComplementarios.getStyle().setDisplay(Display.NONE)
   */
  onTipoCitaChange(tipoCita: number | null): void {
    this.formChange.emit({ tipoCita });

    // Si el nuevo tipo NO requiere campos complementarios, limpiarlos
    const requiereComplementarios =
      tipoCita === TIPOS_CITA_IDS.CAMPO_COMPLEMENTARIO ||
      tipoCita === TIPOS_CITA_IDS.INFORME_COMPLEMENTARIO;
    if (!requiereComplementarios) {
      this.formChange.emit({
        beneficiarioId: null,
        beneficiarioContrato: '',
        beneficiarioIdentificacion: '',
        beneficiarioNombre: '',
        centroTrabajoId: null,
        centroTrabajoNombre: '',
        tareaId: null,
        tareaNombre: ''
      });
    }
  }

  /**
   * Legacy: Centro de trabajo requiere beneficiario seleccionado
   */
  onBuscarCentroTrabajo(): void {
    if (!this.form.beneficiarioId) {
      // El facade mostrará el mensaje de advertencia
      return;
    }
    this.buscarCentroTrabajo.emit();
  }
}
